/**
 * The reporting reads: the summary, cost by plant, worker records and the fund.
 *
 * All of it aggregates in SQL. The summary reads `day_totals` and
 * `plant_day_totals` — tens of rows for a month — rather than walking every
 * timesheet entry, which is what keeps it inside the Worker's 10ms.
 */

import { request } from './api';

export type RangeMode = 'day' | 'week' | 'month' | 'cal' | 'prev';

export interface SummaryDay {
  work_date: string;
  labour_rs: number;
  diesel_rs: number;
  litres_x100: number;
  misc_rs: number;
  advances_rs: number;
  cost_rs: number;
  cash_out_rs: number;
  workers_on_site: number;
  workers_absent: number;
  closed: number;
}

export interface SummaryPlant {
  plant_id: string;
  code: string;
  name: string;
  litres_x100: number;
  diesel_rs: number;
  misc_rs: number;
  total_rs: number;
}

/** Cost by cost code for the period. `cost_code_id` '' is cost entered with none. */
export interface SummaryCostCode {
  cost_code_id: string;
  code: string | null;
  name: string | null;
  is_active: number | null;
  labour_rs: number;
  diesel_rs: number;
  misc_rs: number;
  total_rs: number;
}

export interface SummaryView {
  from: string;
  to: string;
  days: SummaryDay[];
  plants: SummaryPlant[];
  /** Absent from a Worker older than cost codes. */
  costCodes?: SummaryCostCode[];
  totals: {
    labourRs: number;
    dieselRs: number;
    miscRs: number;
    costRs: number;
    cashOutRs: number;
    workingDays: number;
  };
}

export interface WorkerRecordRow {
  worker_id: string;
  worker_name: string;
  roles_label: string | null;
  earned_rs: number;
  unpaid_earned_rs: number;
  advances_rs: number;
  unpaid_advances_rs: number;
  fund_credited_rs: number;
  first_unpaid_date: string | null;
}

export interface WorkerRecord {
  worker: { id: string; name: string };
  days: {
    work_date: string; role_name: string | null; pay_basis: 'day' | 'trip';
    hours_tenths: number; ot_tenths: number; salary_rs: number; ot_rs: number;
    allowance_rs: number; sunday_allowance_rs: number; allowance_reason: string | null;
    earned_rs: number; base_earned_rs: number; status: string;
    pay_run_id: string | null; run_status: string | null; run_period_to: string | null;
  }[];
  advances: {
    id: string; work_date: string; amount_rs: number; note: string | null;
    pay_week_ending: string; signed_at: string | null; signed: number;
    paid_by_name: string; role_name: string | null; week_rs: number;
    approved_by: string | null; approved_at: string | null; approved_by_name: string | null;
  }[];
  contributions: {
    pay_run_id: string; base_rs: number; epf_employee_rs: number; epf_employer_rs: number;
    etf_rs: number; epf_total_rs: number; accrued_at: string; epf_no: string | null;
    period_month: string | null; paid_on: string | null; reference: string | null;
    period_from: string | null; period_to: string | null;
  }[];
  payRuns: {
    id: string; period_from: string; period_to: string; status: string;
    net_rs: number; gross_rs: number; advances_rs: number; epf_employee_rs: number;
  }[];
  memberships: { epf_no: string; member_from: string; stopped_on: string | null }[];
  roles: { name: string; base_rs: number; addon_rs: number; daily_rate_rs: number }[];
}

export interface FundView {
  outstanding: {
    worker_id: string; worker_name: string; epf_no: string | null;
    base_rs: number; epf_employee_rs: number; epf_employer_rs: number;
    etf_rs: number; runs: number;
  }[];
  totals: {
    baseRs: number; epfEmployeeRs: number; epfEmployerRs: number; etfRs: number;
    epfDueRs: number; etfDueRs: number; epfPaidRs: number; etfPaidRs: number;
    epfOwedRs: number; etfOwedRs: number;
  };
  payments: {
    id: string; period_month: string; paid_on: string; reference: string | null;
    epf_rs: number; etf_rs: number; member_count: number; created_by_name: string;
  }[];
  rates: {
    epf_employee_pct_x100: number;
    epf_employer_pct_x100: number;
    etf_pct_x100: number;
  } | null;
}

export interface ScheduleView {
  payment: {
    id: string; period_month: string; paid_on: string; reference: string | null;
    epf_rs: number; etf_rs: number; member_count: number; created_by_name: string;
    /** Who recorded it (only the owner can) and when — stamped as "Paid by". */
    created_by: string; created_at: string;
  };
  lines: {
    worker_id: string; worker_name: string; epf_no: string | null; base_rs: number;
    epf_employee_rs: number; epf_employer_rs: number; etf_rs: number; epf_total_rs: number;
    period_from: string; period_to: string;
  }[];
}

function rangeQuery(range: { mode?: RangeMode; from?: string; to?: string }): string {
  if (range.mode) return `?mode=${range.mode}`;
  if (range.from && range.to) return `?from=${range.from}&to=${range.to}`;
  return '';
}

export const reportsApi = {
  summary: (range: { mode?: RangeMode; from?: string; to?: string }) =>
    request<SummaryView>(`/summary${rangeQuery(range)}`),

  plantDetail: (plantId: string, range: { mode?: RangeMode; from?: string; to?: string }) =>
    request<{
      plantId: string; from: string; to: string;
      days: { work_date: string; litres_x100: number; diesel_rs: number; misc_rs: number; total_rs: number }[];
      miscItems: { work_date: string; description: string; category_name: string; amount_rs: number }[];
    }>(`/summary/plants/${plantId}${rangeQuery(range)}`),

  workerRecords: () => request<{ workers: WorkerRecordRow[] }>('/workers/records'),

  workerRecord: (workerId: string) => request<WorkerRecord>(`/workers/${workerId}/record`),

  // ------------------------------------------------------------- funds
  funds: () => request<FundView>('/funds'),

  /** Amounts are never sent — the Worker derives them from the accruals. */
  recordPayment: (body: {
    periodMonth: string;
    paidOn: string;
    reference: string | null;
    workerIds: string[];
  }) =>
    request<{ id: string; epfRs: number; etfRs: number; memberCount: number }>(
      '/funds/payments',
      { method: 'POST', body },
    ),

  schedule: (paymentId: string) => request<ScheduleView>(`/funds/payments/${paymentId}`),

  voidPayment: (paymentId: string, reason?: string) =>
    request<void>(`/funds/payments/${paymentId}`, {
      method: 'DELETE',
      body: { reason: reason ?? null },
    }),

  fundStatement: () =>
    request<{
      runs: {
        id: string; period_from: string; period_to: string; base_rs: number;
        epf_employee_rs: number; epf_employer_rs: number; etf_rs: number;
      }[];
      payments: {
        period_month: string; paid_on: string; reference: string | null;
        epf_rs: number; etf_rs: number; member_count: number;
      }[];
    }>('/funds/statement'),
};
